import {
  buildDigestModel,
  type ClassifiableItem,
  type ClassifiedItem,
  type ConversationThread,
  classifyNotification,
  createTriageStore,
  type DecryptedNotification,
  type DigestModel,
  gatherDigestItems,
  narrateLead,
  type StorageAdapter,
  type TriageResult,
} from "@reflecto/client-core";
import type { AIProvider } from "@reflecto/client-core/ai";
import { useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 600;
// Hard cap on items classified per pass. On-device inference is ~1-40s PER item depending on hardware
// (CPU-fallback machines are tens of seconds), and the pass is sequential — so a big batch reads as
// "stuck" for many minutes. Cap small and pick the most-recent items; the digest fills incrementally and
// the rest are summarized on the next pass / as the window rolls. (Was 30; lowered after field reports of
// ~40s/item making a 30-item pass take 15-20 min.)
const MAX_ITEMS = 8;
// Per-item classify ceiling: skip a pathologically slow/hung prompt so one item can't wedge the whole
// sequential queue. Generous enough that a normal slow-hardware classify (~40s, ~86s on a cold first run)
// still completes; only true hangs/contention are cut. The skipped item stays uncached and is retried next
// pass. Overridable in tests.
const CLASSIFY_TIMEOUT_MS = 120_000;

export interface UseTriageDriverOptions {
  provider: AIProvider;
  adapter: StorageAdapter;
  /** True only when availability === "available" — the host computes this. */
  enabled: boolean;
  loadNotifications: () => Promise<DecryptedNotification[]>;
  loadConversations: () => Promise<ConversationThread[]>;
  prefs: { hiddenApps: string[] };
  windowMs: number;
  outputLanguage?: string;
  /**
   * Host-derived signal that changes whenever the underlying notifications/conversations change —
   * including async hydration on a cold load. `adapter.onChanged` only fires on storage WRITES, so a
   * returning user whose notifications were merely READ from storage this session emits no change event;
   * the cold-start pass then runs on not-yet-hydrated (empty) loaders and the empty digest would stick.
   * Bumping this re-runs the pass once the host's data is actually present. Hosts derive it cheaply
   * (e.g. notification + conversation counts).
   */
  dataVersion?: unknown;
  /** Injectable clock for deterministic tests. */
  now?: () => number;
  /** Per-item classify timeout override (ms); defaults to CLASSIFY_TIMEOUT_MS. Test seam. */
  classifyTimeoutMs?: number;
}

export interface TriageDriverState {
  /** Live digest: seeded from persisted glanceDigest, then ambiently refreshed. null until first build/seed. */
  digest: DigestModel | null;
  /** True while the lead is mid-stream — the host aria-busies the live region to mute per-chunk announcements. */
  leadStreaming: boolean;
}

export function useTriageDriver(opts: UseTriageDriverOptions): TriageDriverState {
  const [digest, setDigest] = useState<DigestModel | null>(null);
  const [leadStreaming, setLeadStreaming] = useState(false);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Set by the main effect to its single-flight pass runner so the dataVersion effect below can re-trigger
  // a pass without tearing the main effect down (which would abort in-flight classification). Null while
  // disabled / unmounted.
  const triggerPassRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Instant-on: show last session's digest immediately. `updatedAt` is intentionally not consulted —
    // freshness is delegated to the driver, which re-classifies + rebuilds on data change while enabled.
    optsRef.current.adapter
      .get("glanceDigest")
      .then((s) => {
        if (!cancelled && s.glanceDigest) setDigest(s.glanceDigest.model);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!opts.enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    // Capture adapter once so store + persist share one backend; dynamic opts are re-read from optsRef below.
    const adapter = optsRef.current.adapter;
    const store = createTriageStore(adapter);
    // Re-reading getter (not captured) so a swapped `now` prop is honoured — the injectable-clock test seam.
    const now = () => (optsRef.current.now ?? Date.now)();

    let debounce: ReturnType<typeof setTimeout> | undefined;
    let leadAbort: AbortController | undefined;

    const scheduleRebuild = (items: ClassifiableItem[]) => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = undefined; // fired — the var again truthily means "a rebuild is pending"
        // Fire-and-forget: a storage failure mid-rebuild is swallowed; the next pass retries.
        rebuild(items).catch(() => {});
      }, DEBOUNCE_MS);
    };

    const rebuild = async (items: ClassifiableItem[]) => {
      if (signal.aborted) return;
      const o = optsRef.current;
      // Claim narration ownership before any await so "last rebuild to start" wins, not "last to settle getAll".
      leadAbort?.abort();
      leadAbort = new AbortController();
      const tie = leadAbort.signal;
      const cached = await store.getAll();
      // Bail if torn down OR superseded mid-getAll — else a stale setLeadStreaming(true) strands aria-busy.
      if (signal.aborted || tie.aborted) return;
      const classified: ClassifiedItem[] = [];
      let pending = 0;
      for (const item of items) {
        const triage = cached[item.id];
        if (triage) classified.push({ item, triage });
        else pending++;
      }
      // scanned = total in-window items (classified + still-pending), not just the classified count.
      const model = buildDigestModel(classified, classified.length + pending, pending);
      setDigest(model);
      // Track the streamed lead in a local (never mutate the object already handed to setDigest).
      let lead = model.lead;
      const willNarrate = model.needsYou.length > 0 || model.quiet.length > 0;
      // Reflect this (live) rebuild's stream state. false also clears any leftover true from a
      // superseded narrating rebuild that this non-narrating one replaced.
      setLeadStreaming(willNarrate);
      try {
        if (willNarrate) {
          for await (const text of narrateLead(o.provider, model, {
            signal: tie,
            outputLanguage: o.outputLanguage,
          })) {
            if (tie.aborted || signal.aborted) break;
            lead = text;
            setDigest((prev) => (prev ? { ...prev, lead } : { ...model, lead }));
          }
        }
      } catch {
        // narrateLead swallows aborts; a real failure leaves the deterministic model (lead stays null).
      } finally {
        // Clear busy only when this rebuild actually streamed AND still owns the live tie — a superseded
        // rebuild (aborted `tie`) or a torn-down effect must not flip the flag under the fresher stream.
        if (willNarrate && !tie.aborted && !signal.aborted) setLeadStreaming(false);
      }
      // Bail if EITHER the effect was torn down OR a newer rebuild superseded this one (aborted `tie`) —
      // otherwise a stale rebuild could persist its partial lead after the fresher write.
      if (signal.aborted || tie.aborted) return;
      // Persist a fresh object carrying the settled lead — `model` itself is never mutated. Uses the
      // captured `adapter` (same backend as `store`), not o.adapter.
      await adapter.set({ glanceDigest: { model: { ...model, lead }, updatedAt: now() } });
    };

    const runPass = async () => {
      const o = optsRef.current;
      const [notifications, conversations] = await Promise.all([
        o.loadNotifications(),
        o.loadConversations(),
      ]).catch(() => [[], []] as [DecryptedNotification[], ConversationThread[]]);
      if (signal.aborted) return;
      const windowStart = now() - o.windowMs;
      // Most-recent first, then cap: on slow hardware we can only afford a few classifies per pass, so spend
      // them on the freshest items. The rest fill in on later passes / as the window rolls.
      const items = gatherDigestItems({
        notifications,
        conversations,
        windowStart,
        prefs: { hiddenApps: o.prefs.hiddenApps },
      })
        .sort((a, b) => b.input.timestamp - a.input.timestamp)
        .slice(0, MAX_ITEMS);

      await store.prune(items.map((i) => i.id));
      if (signal.aborted) return;
      scheduleRebuild(items);

      const timeoutMs = o.classifyTimeoutMs ?? CLASSIFY_TIMEOUT_MS;
      for (const item of items) {
        if (signal.aborted) return;
        const existing = await store.get(item.id);
        if (existing) continue;
        let result: TriageResult;
        try {
          // Cap each classify so a hung/contended on-device prompt can't wedge the sequential queue. The
          // timeout aborts only this item (combined with the pass signal); a skipped item stays uncached and
          // is retried on the next pass.
          const itemSignal = AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]);
          result = await classifyNotification(o.provider, item.input, { signal: itemSignal });
        } catch {
          continue;
        }
        if (signal.aborted) return;
        await store.set(item.id, result);
        scheduleRebuild(items);
      }
    };

    // Single-flight: a re-trigger during an in-flight pass coalesces into one trailing re-run rather than
    // running runPass concurrently (which would double-classify uncached items and race store.prune).
    let passInFlight = false;
    let passQueued = false;
    const runPassSingleFlight = async () => {
      if (passInFlight) {
        passQueued = true;
        return;
      }
      passInFlight = true;
      try {
        do {
          passQueued = false;
          await runPass();
        } while (passQueued && !signal.aborted);
      } finally {
        passInFlight = false;
      }
    };

    // Keep the digest live: notifications/conversations changes only — our own writes are excluded (no self-loop).
    let passDebounce: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = adapter.onChanged((changes) => {
      if (signal.aborted) return;
      if (!("notifications" in changes || "conversations" in changes)) return;
      if (passDebounce) clearTimeout(passDebounce);
      passDebounce = setTimeout(() => {
        passDebounce = undefined;
        if (signal.aborted) return; // teardown can land between the fire and cleanup's clearTimeout
        runPassSingleFlight().catch(() => {});
      }, DEBOUNCE_MS);
    });

    // Expose the runner so the dataVersion effect can re-trigger without rebuilding this effect. The
    // initial pass is fired by that effect (it runs on mount too), so there's no standalone call here —
    // a standalone call would double-run on mount alongside the effect.
    triggerPassRef.current = () => {
      runPassSingleFlight().catch(() => {});
    };

    return () => {
      controller.abort();
      unsubscribe();
      triggerPassRef.current = null;
      leadAbort?.abort();
      if (debounce) clearTimeout(debounce);
      if (passDebounce) clearTimeout(passDebounce);
      // Teardown aborts both signals, so the rebuild `finally` skips its clear — reset here so a
      // disable mid-narration can't strand aria-busy=true on the live region for the session.
      setLeadStreaming(false);
    };
  }, [opts.enabled]);

  // Run the initial pass and re-run whenever the host's data changes (incl. async hydration on cold load).
  // adapter.onChanged covers live storage WRITES; this covers data that was only READ into the host (a
  // returning user's pre-existing notifications), which emits no change event. single-flight + debounce in
  // the runner coalesce any overlap with the onChanged path.
  // biome-ignore lint/correctness/useExhaustiveDependencies: triggerPassRef is a stable ref; the pass reads
  // live opts via optsRef. Re-running on every opts identity change would thrash; enabled + dataVersion are
  // the intended triggers.
  useEffect(() => {
    if (!opts.enabled) return;
    triggerPassRef.current?.();
  }, [opts.enabled, opts.dataVersion]);

  return { digest, leadStreaming };
}
